/**
 * MazeStriker API helpers. Per-run flow: start today's session, then report each finished run. The server
 * owns the economy — coins depend only on server-clamped `levelsCleared` + `coinsCollected` and the per-day
 * cap, never on a client-supplied amount. `score` is advisory (best-score stat / UI) and never banks.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { MAZE_STRIKER_GAME_KEY } from '../constants/mazeStriker.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface MazeStrikerStart {
  coinsPerLevel: number;
  maxPaidPickupsPerRun: number;
  dailyCoinCap: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestScore: number;
}

export interface MazeStrikerEndResult {
  coinsAwarded: number;
  /** New server coin balance; omitted for anonymous users (never banked). */
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestScore: number;
  newBest: boolean;
}

export async function startMazeStriker(apiClient: ApiClient): Promise<MazeStrikerStart> {
  const response = await apiClient<{ success: boolean; data: MazeStrikerStart; message?: string }>(
    API_ENDPOINTS.games.mazeStart(MAZE_STRIKER_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start the game');
  return payload.data;
}

export async function recordMazeStrikerEnd(
  apiClient: ApiClient,
  args: { levelsCleared: number; coinsCollected: number; score: number },
): Promise<MazeStrikerEndResult> {
  const response = await apiClient<{ success: boolean; data: MazeStrikerEndResult; message?: string }>(
    API_ENDPOINTS.games.mazeEnd(MAZE_STRIKER_GAME_KEY),
    { method: 'POST', data: args },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not record the run');
  return payload.data;
}
